import type { ArticleSchema } from 'data-layer';

interface ArticleSchemaScriptProps {
  schema: ArticleSchema;
}

/**
 * Removes empty author fields from Article schema to meet Google Rich Results requirements.
 *
 * Acceptance criteria: Do not render 'url' and 'name' keys if both are missing/undefined.
 * - If both name and url are missing: remove entire author object
 * - If only name is missing: remove name key, keep url
 * - If only url is missing: remove url key, keep name
 */
function cleanAuthorFields(schema: ArticleSchema): ArticleSchema {
  if (!schema.author?.name && !schema.author?.url) {
    const rest = { ...schema };
    delete rest.author;

    return rest;
  }

  return {
    ...schema,
    author: {
      '@type': 'Person',
      ...(schema.author.name && { name: schema.author.name }),
      ...(schema.author.url && { url: schema.author.url }),
    },
  };
}

/**
 * ArticleSchemaScript Component
 *
 * Renders Article schema for blog posts and editorial content.
 * Helps AI search engines (Google AI, Perplexity, ChatGPT) identify and cite content.
 *
 * Ensures clean JSON-LD output by removing undefined/empty author fields.
 */
export function ArticleSchemaScript({ schema }: ArticleSchemaScriptProps) {
  const cleanedSchema = cleanAuthorFields(schema);

  return (
    <script
      id="article-schema-jsonld"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(cleanedSchema) }}
    />
  );
}
